import { mailAuthHeaders } from "@/auth/session";
import { fetchMailEmlBatch, getMailEmlExportUrl } from "@/lib/api/storage";
import { uploadCoffreFile, type CoffreListZone } from "@/lib/suite/coffreSuiteApi";

export type MailEmlArchiveTarget = {
  accountId: number;
  folder: string;
  uid: number;
  subject: string;
  date?: string;
};

export type MailEmlArchiveProgress = {
  done: number;
  total: number;
  label?: string;
};

function sanitizeEmlBase(subject: string, uid: number): string {
  const cleaned = subject.replace(/[<>:"/\\|?*\u0000-\u001f]/g, " ").replace(/\s+/g, " ").trim();
  const base = (cleaned || `message-${uid}`).slice(0, 80).trim();
  return base.replace(/[. ]+$/u, "") || `message-${uid}`;
}

function dayStamp(date?: string): string | null {
  if (!date) return null;
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, "0");
  const d = String(parsed.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function filenameCandidates(target: MailEmlArchiveTarget): string[] {
  const base = sanitizeEmlBase(target.subject, target.uid);
  const names = [`${base}.eml`];
  const day = dayStamp(target.date);
  if (day) names.push(`${base} (${day}).eml`);
  names.push(`${base} (${target.uid}).eml`);
  return names;
}

async function ensureEmlStored(target: MailEmlArchiveTarget): Promise<void> {
  const res = await fetchMailEmlBatch({
    accountId: target.accountId,
    folder: target.folder,
    uids: [target.uid],
    includeAttachments: true,
    warmAttachments: false,
  });
  if (!res.ok) {
    throw new Error(res.message || "Impossible de préparer le message.");
  }
  const item = (res.data.items ?? []).find((entry) => entry.uid === target.uid);
  if (!item || item.error || item.storeError || item.stored !== true) {
    throw new Error(item?.error || "Archive .eml introuvable.");
  }
}

async function downloadEmlBlob(target: MailEmlArchiveTarget): Promise<Blob> {
  const res = await fetch(
    getMailEmlExportUrl({
      accountId: target.accountId,
      folder: target.folder,
      uid: target.uid,
      subject: target.subject,
    }),
    {
      credentials: "include",
      cache: "no-store",
      headers: mailAuthHeaders(),
    }
  );
  if (!res.ok) {
    let message = "Archive .eml introuvable.";
    try {
      const body = (await res.json()) as { message?: string };
      if (body.message) message = body.message;
    } catch {
      /* corps binaire */
    }
    throw new Error(message);
  }
  const blob = await res.blob();
  if (blob.size <= 0) throw new Error("Fichier .eml vide.");
  return blob;
}

async function uploadEmlBlob(
  blob: Blob,
  target: MailEmlArchiveTarget,
  destPath: string,
  zone: CoffreListZone
): Promise<void> {
  let lastMessage = "Enregistrement impossible.";
  for (const filename of filenameCandidates(target)) {
    const res = await uploadCoffreFile({
      file: blob,
      filename,
      path: destPath,
      zone,
    });
    if (res.ok) return;
    lastMessage = res.data.message ?? lastMessage;
    const exists = res.status === 409 || res.data.error === "already_exists";
    if (!exists) break;
  }
  throw new Error(lastMessage);
}

export async function archiveMailMessagesToCoffre(
  targets: MailEmlArchiveTarget[],
  destPath: string,
  zone: CoffreListZone = "vault",
  onProgress?: (progress: MailEmlArchiveProgress) => void
): Promise<{ ok: number; failed: number; errors: string[] }> {
  let done = 0;
  let ok = 0;
  let failed = 0;
  const errors: string[] = [];
  const total = targets.length;

  for (const target of targets) {
    const label = target.subject.trim() || `message-${target.uid}`;
    onProgress?.({ done, total, label });
    try {
      await ensureEmlStored(target);
      const blob = await downloadEmlBlob(target);
      await uploadEmlBlob(blob, target, destPath, zone);
      ok += 1;
    } catch (err) {
      failed += 1;
      errors.push(err instanceof Error ? err.message : "Archivage impossible.");
    }
    done += 1;
    onProgress?.({ done, total, label });
  }

  return { ok, failed, errors };
}
